import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { CollectionItem } from '../types'
import { getCollection, deleteCollectionItem, updateCollectionItem, createTradeRequest } from '../utils/storage'
import styles from './CollectionDetail.module.css'

const conditionLabels: Record<string, string> = {
  new: '新品・未使用',
  like_new: '未使用に近い',
  good: '目立った傷や汚れなし',
  fair: 'やや傷や汚れあり',
  poor: '傷や汚れあり',
}

const categoryLabels: Record<string, string> = {
  acrylic: 'アクリルスタンド',
  penlight: 'ペンライト',
  photo: '写真・ブロマイド',
  towel: 'タオル',
  tshirt: 'Tシャツ',
  other: 'その他',
}

export default function CollectionDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [item, setItem] = useState<CollectionItem | null>(null)
  const [showTradeModal, setShowTradeModal] = useState(false)
  const [tradeForm, setTradeForm] = useState({ offeredItem: '', message: '' })
  const [tradeSent, setTradeSent] = useState(false)

  useEffect(() => {
    if (id) {
      const found = getCollection().find(i => i.id === id)
      setItem(found || null)
    }
  }, [id])

  if (!item) {
    return (
      <div className={styles.container}>
        <p>アイテムが見つかりません</p>
        <Link to="/collection">コレクションに戻る</Link>
      </div>
    )
  }

  const handleToggleTrade = () => {
    updateCollectionItem(item.id, { isTradeAvailable: !item.isTradeAvailable })
    setItem({ ...item, isTradeAvailable: !item.isTradeAvailable })
  }

  const handleDelete = () => {
    if (window.confirm('このアイテムを削除しますか？')) {
      deleteCollectionItem(item.id)
      navigate('/collection')
    }
  }

  const handleTradeRequest = () => {
    if (!tradeForm.offeredItem.trim()) return
    createTradeRequest({
      itemId: item.id,
      itemTitle: item.title,
      offeredItem: tradeForm.offeredItem,
      message: tradeForm.message || undefined,
    })
    setTradeSent(true)
    setShowTradeModal(false)
    setTradeForm({ offeredItem: '', message: '' })
  }

  return (
    <div className={styles.container}>
      <div className={styles.image}>
        <img
          src={item.imageUrl || 'https://placehold.co/400x400?text=No+Image'}
          alt={item.title}
        />
      </div>

      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title}>{item.title}</h1>
          {item.isTradeAvailable && (
            <span className={styles.tradeBadge}>交換可</span>
          )}
        </div>

        <div className={styles.section}>
          <h2>アイテム情報</h2>
          <dl className={styles.details}>
            <dt>カテゴリ</dt>
            <dd>{categoryLabels[item.category] || item.category}</dd>
            {item.artist && (
              <>
                <dt>アーティスト</dt>
                <dd>{item.artist}</dd>
              </>
            )}
            {item.group && (
              <>
                <dt>グループ</dt>
                <dd>{item.group}</dd>
              </>
            )}
            <dt>状態</dt>
            <dd>{conditionLabels[item.condition]}</dd>
            <dt>登録日</dt>
            <dd>{new Date(item.createdAt).toLocaleDateString('ja-JP')}</dd>
          </dl>
        </div>

        {item.memo && (
          <div className={styles.section}>
            <h2>メモ</h2>
            <p className={styles.memo}>{item.memo}</p>
          </div>
        )}

        {item.isTradeAvailable && item.wantedItems && item.wantedItems.length > 0 && (
          <div className={styles.section}>
            <h2>交換希望</h2>
            <p className={styles.wantedDescription}>このアイテムと交換したいもの</p>
            <ul className={styles.wantedList}>
              {item.wantedItems.map((wanted, index) => (
                <li key={index}>{wanted}</li>
              ))}
            </ul>
          </div>
        )}

        {item.isTradeAvailable && (
          <div className={styles.tradeRequestSection}>
            {tradeSent ? (
              <p className={styles.tradeSentMessage}>交換依頼を送信しました！</p>
            ) : (
              <button
                className={styles.tradeRequestButton}
                onClick={() => setShowTradeModal(true)}
              >
                交換を依頼する
              </button>
            )}
          </div>
        )}

        <div className={styles.actions}>
          <button
            className={`${styles.tradeButton} ${item.isTradeAvailable ? styles.active : ''}`}
            onClick={handleToggleTrade}
          >
            {item.isTradeAvailable ? '交換可能' : '交換不可'}
          </button>
          <Link to={`/collection/${item.id}/edit`} className={styles.editButton}>
            編集
          </Link>
          <button className={styles.deleteButton} onClick={handleDelete}>
            削除
          </button>
        </div>
      </div>

      {showTradeModal && (
        <div className={styles.modalOverlay} onClick={() => setShowTradeModal(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h2>交換を依頼する</h2>
            <p className={styles.modalTarget}>「{item.title}」との交換</p>

            <div className={styles.modalField}>
              <label>交換に出すアイテム</label>
              <input
                type="text"
                value={tradeForm.offeredItem}
                onChange={e => setTradeForm({ ...tradeForm, offeredItem: e.target.value })}
                placeholder="例: ○○のアクスタ"
              />
            </div>

            <div className={styles.modalField}>
              <label>メッセージ（任意）</label>
              <textarea
                value={tradeForm.message}
                onChange={e => setTradeForm({ ...tradeForm, message: e.target.value })}
                placeholder="交換条件など"
                rows={3}
              />
            </div>

            <div className={styles.modalActions}>
              <button
                className={styles.modalCancel}
                onClick={() => setShowTradeModal(false)}
              >
                キャンセル
              </button>
              <button
                className={styles.modalSubmit}
                onClick={handleTradeRequest}
                disabled={!tradeForm.offeredItem.trim()}
              >
                依頼を送る
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
